import { useState, useMemo } from "react";

export default function Contact() {
  const [subject, setSubject] = useState<string>("製品について");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");

  const subjectOptions = ["製品について", "取扱店について", "取材・メディア", "その他"];

  const mailtoHref = useMemo(() => {
    const body = [
      `お名前：${name}`,
      `メールアドレス：${email}`,
      `電話番号：${phone}`,
      `お問い合わせ種別：${subject}`,
      "",
      "【お問い合わせ内容】",
      message,
    ].join("\n");

    const params = new URLSearchParams();
    params.set("subject", subject);
    params.set("body", body);

    return `mailto:info@raigeki-hayabusa.com?${params.toString()}`;
  }, [name, email, phone, subject, message]);

  return (
    <section
      id="contact"
      className="relative w-full py-24 md:py-32 bg-background-100 border-t border-background-200/60"
    >
      <div className="w-full px-6 md:px-10 max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left side */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-[2px] bg-primary-500"></span>
              <span
                className="text-primary-500 text-xs tracking-[0.4em]"
                style={{ fontFamily: "var(--font-label)" }}
              >
                CONTACT
              </span>
            </div>
            <h4
              className="text-foreground-950 text-5xl md:text-6xl leading-[0.95] mb-6"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              <a href="#contact" className="hover:text-primary-500 transition-colors cursor-pointer">
                お問い合わせ<span className="text-primary-500">.</span>
              </a>
            </h4>
            <p className="text-foreground-700 text-base leading-relaxed max-w-md mb-10">
              製品仕様、取扱店、フィールドテスト等、
              お気軽にお問い合わせください。
            </p>

            <div className="space-y-5 border-t border-background-200/60 pt-8">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-background-200 text-primary-500">
                  <i className="ri-map-pin-2-line text-lg"></i>
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.3em] text-foreground-9500 mb-1">
                    HEADQUARTERS
                  </div>
                  <div className="text-foreground-900 text-sm">
                    島根県松江市
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-background-200 text-primary-500">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.3em] text-foreground-9500 mb-1">
                    EMAIL
                  </div>
                  <div className="text-foreground-900 text-sm">info@raigeki-hayabusa.com</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-background-200 text-primary-500">
                  <i className="ri-time-line text-lg"></i>
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.3em] text-foreground-9500 mb-1">
                    HOURS
                  </div>
                  <div className="text-foreground-900 text-sm">
                    お問い合わせメール受信後、４８時間以内にご連絡いたします
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right form */}
          <div className="lg:col-span-7">
            <div className="bg-background-50 border border-background-200/60 rounded-lg p-6 md:p-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-[10px] tracking-[0.3em] text-foreground-9500 mb-2">
                    お名前 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="山田 太郎"
                    className="w-full bg-background-100 border border-background-200/60 rounded-md px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-9500 focus:outline-none focus:border-primary-500 transition"
                  />
                </div>
                <div>
                  <label className="block text-[10px] tracking-[0.3em] text-foreground-9500 mb-2">
                    メールアドレス <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full bg-background-100 border border-background-200/60 rounded-md px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-9500 focus:outline-none focus:border-primary-500 transition"
                  />
                </div>
                <div>
                  <label className="block text-[10px] tracking-[0.3em] text-foreground-9500 mb-2">
                    電話番号
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="090-0000-0000"
                    className="w-full bg-background-100 border border-background-200/60 rounded-md px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-9500 focus:outline-none focus:border-primary-500 transition"
                  />
                </div>
                <div>
                  <label className="block text-[10px] tracking-[0.3em] text-foreground-9500 mb-2">
                    お問い合わせ種別
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {subjectOptions.map((s) => (
                      <button
                        type="button"
                        key={s}
                        onClick={() => setSubject(s)}
                        className={`text-xs px-3 py-2 rounded-full border transition whitespace-nowrap cursor-pointer ${
                          subject === s
                            ? "bg-primary-500 border-primary-500 text-background-50"
                            : "bg-background-100 border-background-200/60 text-foreground-700 hover:border-primary-500/60"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-5">
                <label className="block text-[10px] tracking-[0.3em] text-foreground-9500 mb-2">
                  メッセージ <span className="text-primary-500">*</span>
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  maxLength={500}
                  rows={5}
                  placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                  className="w-full bg-background-100 border border-background-200/60 rounded-md px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-9500 focus:outline-none focus:border-primary-500 transition resize-none"
                />
              </div>

              <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                <div className="text-xs text-foreground-9500 leading-relaxed">
                  ※ ご入力いただいた情報は当社のプライバシーポリシーに従って取り扱います。
                </div>
                <a
                  href={mailtoHref}
                  className="group inline-flex items-center gap-3 pl-6 pr-2 py-2 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 whitespace-nowrap cursor-pointer transition"
                >
                  <span
                    className="text-xs tracking-[0.3em]"
                    style={{ fontFamily: "var(--font-label)" }}
                  >
                    送信する
                  </span>
                  <span className="w-10 h-10 flex items-center justify-center rounded-full bg-background-50 text-primary-500 group-hover:rotate-45 transition-transform">
                    <i className="ri-arrow-right-up-line"></i>
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}